// src/pages/session/SessionEndPage.js (Final Adaptive Design)

import React, { useState, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import MainNavbar from '../../components/MainNavbar';
import MainFooter from '../../components/MainFooter';
import WordCard from '../../components/course/WordCard';
import SentenceCard from '../../components/course/SentenceCard';
import { useModal } from '../../context/ModalContext';
import DetailsModal from '../../components/modals/DetailsModal/DetailsModal';
import { useMetadata } from '../../contexts/MetadataProvider';
import { SHIELD_EARN_DAYS } from '../../utils/streak';

import { CheckCircleIcon, XCircleIcon, BookOpenIcon, ChevronDownIcon, SparklesIcon } from '@heroicons/react/24/solid';

function StatCard({ icon, value, label }) {
  return (
    <div className="bg-background-secondary rounded-xl shadow-md pb-6 text-center flex flex-col justify-center items-center h-full">
      {/* Icon Badge: Creates a modern, celebratory feel */}
      <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10">
        {/* We need to clone the icon element to apply new classes */}
        {React.cloneElement(icon, { className: 'h-8 w-8 text-primary' })}
      </div>

      {/* Main Value: The hero of the card */}
      <p className="text-5xl font-bold text-content-primary">{value}</p>

      {/* Descriptive Label */}
      <p className="mt-1 text-base text-content-secondary">{label}</p>
    </div>
  );
}

function SessionEndPageFinal() {
  const location = useLocation();
  const navigate = useNavigate();
  const { showModal, hideModal } = useModal();
  const { correctWords = [], wrongWords = [], correctSentences = [], wrongSentences = [], type, shieldEarned } = location.state || {};

  const isCramSession = type === 'cram_sentences' || type === 'cram_words';
  const isRecentUnlocksSession = type === 'recent_unlocks_sentences' || type === 'recent_unlocks_words';
  const isExtraStudySession = isCramSession || isRecentUnlocksSession;
  const isWordSession = type === 'words' || type === 'cram_words' || type === 'recent_unlocks_words';
  const { learnedWordIds, learnedSentenceIds } = useMetadata();
  const learnedIds = isWordSession ? learnedWordIds : learnedSentenceIds;
  const isLearningSession = type === 'learn_sentences';
  const correctItems = isLearningSession || type === 'sentences' || type === 'cram_sentences' || type === 'recent_unlocks_sentences' ? correctSentences : correctWords;
  const wrongItems = isWordSession ? wrongWords : wrongSentences;

  const [isCorrectItemsVisible, setIsCorrectItemsVisible] = useState(false);
  const [isCramCorrectVisible, setIsCramCorrectVisible] = useState(false);

  const sessionStats = useMemo(() => {
    const totalCorrect = correctItems.length;
    const totalWrong = wrongItems.length;
    const totalItems = totalCorrect + totalWrong;
    const accuracy = totalItems > 0 ? Math.round((totalCorrect / totalItems) * 100) : 100;
    return { totalCorrect, totalWrong, totalItems, accuracy };
  }, [correctItems, wrongItems]);

  // The session already decided whether it earned a 盾 (via willEarnShieldToday) and
  // passed the result in router state, so there's nothing to recompute here.
  const showShieldEarned = !!shieldEarned;

  const handleCardClick = useCallback(
    (item) => {
      showModal(<DetailsModal item={item} handleClose={hideModal} />);
    },
    [showModal, hideModal]
  );

  const getGridCols = () => {
    if (isWordSession) return 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6';
    return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
  };

  const renderItems = (items) => {
    if (items.length === 0) return null;
    return (
      <div className={`pt-6 grid gap-4 ${getGridCols()}`}>
        {items.map((item) => (
          <div key={item.id} className="p-1">
            {isWordSession ? (
              <WordCard word={item} onCardClick={() => handleCardClick(item)} learnedIds={learnedIds} />
            ) : (
              <SentenceCard sentence={item} onCardClick={() => handleCardClick(item)} learnedIds={learnedIds} />
            )}
          </div>
        ))}
      </div>
    );
  };

  // --- RENDER FUNCTION: Now uses the new StatCard component for a cleaner look and code ---
  const renderSummary = () => {
    const reviewTitle = sessionStats.accuracy >= 95 ? 'Excellent!' : sessionStats.accuracy >= 80 ? 'Great Session!' : 'Good Effort!';

    return (
      <div className="text-center">
        <h1 className="text-5xl font-extrabold text-content-primary tracking-tight">
          {isCramSession ? 'Cram Complete!' : isRecentUnlocksSession ? 'Practice Complete!' : isLearningSession ? 'New Sentences Learned!' : reviewTitle}
        </h1>
        <p className="mt-3 text-lg text-content-secondary">
          {isCramSession
            ? `You reviewed ${sessionStats.totalItems} item${sessionStats.totalItems !== 1 ? 's' : ''} from your recent mistakes.`
            : isRecentUnlocksSession
            ? `You practiced ${sessionStats.totalItems} recently unlocked item${sessionStats.totalItems !== 1 ? 's' : ''}.`
            : isLearningSession
            ? `You've successfully added ${sessionStats.totalCorrect} new sentences to your reviews.`
            : "You've completed your review. Here are your results."}
        </p>

        {isLearningSession ? (
          <div className="mt-8 max-w-xs mx-auto">
            <StatCard icon={<SparklesIcon />} value={sessionStats.totalCorrect} label="New Sentences Added" />
          </div>
        ) : isExtraStudySession ? (
          <>
            <div className="mt-8 max-w-2xl mx-auto">
              <div className="flex justify-between mb-1">
                <span className="text-base font-medium text-primary">Accuracy</span>
                <span className="text-sm font-medium text-primary">{sessionStats.accuracy}%</span>
              </div>
              <div className="w-full bg-background-tertiary rounded-full h-2.5">
                <div className="bg-primary h-2.5 rounded-full" style={{ width: `${sessionStats.accuracy}%` }}></div>
              </div>
            </div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <StatCard icon={<CheckCircleIcon />} value={sessionStats.totalCorrect} label="Correct" />
              <StatCard icon={<XCircleIcon />} value={sessionStats.totalWrong} label="Incorrect" />
              <StatCard icon={<BookOpenIcon />} value={sessionStats.totalItems} label="Total Reviewed" />
            </div>
          </>
        ) : (
          <>
            <div className="mt-8 max-w-2xl mx-auto">
              <div className="flex justify-between mb-1">
                <span className="text-base font-medium text-primary">Accuracy</span>
                <span className="text-sm font-medium text-primary">{sessionStats.accuracy}%</span>
              </div>
              <div className="w-full bg-background-tertiary rounded-full h-2.5">
                <div className="bg-primary h-2.5 rounded-full" style={{ width: `${sessionStats.accuracy}%` }}></div>
              </div>
            </div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <StatCard icon={<CheckCircleIcon />} value={sessionStats.totalCorrect} label="Correct" />
              <StatCard icon={<XCircleIcon />} value={sessionStats.totalWrong} label="Incorrect" />
              <StatCard icon={<BookOpenIcon />} value={sessionStats.totalItems} label="Total Reviewed" />
            </div>
          </>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (isLearningSession) {
      return (
        <div className="bg-background-secondary rounded-xl shadow-lg p-6 sm:p-8">
          <h2 className="text-2xl font-bold text-content-primary">Your New Sentences</h2>
          <p className="mt-1 text-content-secondary">These will now appear in your future review sessions.</p>
          {renderItems(correctItems)}
        </div>
      );
    }

    if (isExtraStudySession) {
      const struggledNote = isCramSession
        ? "You got there — these took a few tries. Keep doing cram sessions until they come first try."
        : "You got there — these took a few tries. Keep practicing to build confidence.";
      const correctNote = isCramSession
        ? "These items are still in your cram list until you get them right in a review session."
        : "Great work — these are solid. Keep them in your regular review rotation.";
      return (
        <div className="space-y-12">
          {wrongItems.length > 0 && (
            <div className="bg-background-secondary rounded-xl shadow-lg p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-yellow-500">Eventually Got It Right</h2>
              <p className="mt-1 text-content-secondary">{struggledNote}</p>
              {renderItems(wrongItems)}
            </div>
          )}
          {correctItems.length > 0 && (
            <div className="bg-background-secondary rounded-xl shadow-lg p-6 sm:p-8">
              <button onClick={() => setIsCramCorrectVisible(!isCramCorrectVisible)} className="w-full flex justify-between items-center text-left">
                <h2 className="text-xl sm:text-2xl font-bold text-green-500">Got It Right First Try</h2>
                <div className="flex items-center flex-shrink-0 ml-2">
                  <span className="mr-1 hidden sm:inline text-content-secondary whitespace-nowrap text-sm sm:text-base">{correctItems.length} items</span>
                  <ChevronDownIcon
                    className={`h-6 w-6 text-content-secondary transition-transform duration-300 ${isCramCorrectVisible ? 'rotate-180' : ''}`}
                  />
                </div>
              </button>
              <p className="mt-1 text-content-secondary">{correctNote}</p>
              {isCramCorrectVisible && renderItems(correctItems)}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-12">
        {wrongItems.length > 0 && (
          <div className="bg-background-secondary rounded-xl shadow-lg p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-red-500">Items to Focus On</h2>
            <p className="mt-1 text-content-secondary">Pay extra attention to these in your next sessions.</p>
            {renderItems(wrongItems)}
          </div>
        )}
        {correctItems.length > 0 && (
          <div className="bg-background-secondary rounded-xl shadow-lg p-6 sm:p-8">
            <button onClick={() => setIsCorrectItemsVisible(!isCorrectItemsVisible)} className="w-full flex justify-between items-center text-left">
              <h2 className="text-xl sm:text-2xl font-bold text-green-500">Correctly Answered Items</h2>
              <div className="flex items-center flex-shrink-0 ml-2">
                <span className="mr-1 hidden sm:inline text-content-secondary whitespace-nowrap text-sm sm:text-base">{sessionStats.totalCorrect} items</span>
                <ChevronDownIcon
                  className={`h-6 w-6 text-content-secondary transition-transform duration-300 ${isCorrectItemsVisible ? 'rotate-180' : ''}`}
                />
              </div>
            </button>
            {isCorrectItemsVisible && renderItems(correctItems)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-background-primary">
      <MainNavbar />
      <main className="flex-grow">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          {showShieldEarned && (
            <div className="mb-10 max-w-2xl mx-auto flex items-center gap-4 rounded-2xl border-2 border-sky-400/50 bg-sky-500/10 p-5 animate-fade-in">
              <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-sky-500 shadow-sm">
                <span lang="ja" className="text-2xl font-bold leading-none text-white">盾</span>
              </div>
              <div className="text-left">
                <p className="text-lg font-bold text-content-primary">Protection earned!</p>
                <p className="mt-0.5 text-sm text-content-secondary">
                  {SHIELD_EARN_DAYS} days in a row. It&apos;s kept automatically and protects your streak if you miss a day.
                </p>
              </div>
            </div>
          )}
          {renderSummary()}
          <div className="mt-16">{renderContent()}</div>
          <div className="mt-16 text-center flex flex-col sm:flex-row gap-4 justify-center items-center">
            {isExtraStudySession && (
              <button
                onClick={() => {
                  const cramPath =
                    type === 'cram_words' ? '/session/cram/words' :
                    type === 'cram_sentences' ? '/session/cram/sentences' :
                    type === 'recent_unlocks_words' ? '/session/recent-unlocks/words' :
                    '/session/recent-unlocks/sentences';
                  navigate(cramPath);
                }}
                className="inline-block px-10 py-4 border border-transparent text-lg font-medium rounded-md text-white bg-primary hover:opacity-95 shadow-lg transform transition hover:scale-105"
              >
                Go Again
              </button>
            )}
            <Link
              to="/dashboard"
              className="inline-block px-10 py-4 border-2 border-primary text-lg font-medium rounded-md text-primary bg-transparent hover:bg-primary hover:text-white shadow-lg transform transition hover:scale-105"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </main>
      <MainFooter />
    </div>
  );
}

export default SessionEndPageFinal;
